import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  arrayRemove,
  collection,
  deleteField,
  doc,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

import ButtonCustom from '@/components/common/ui/button';
import { ConversationType, MessageType, UserType } from '@/types/chat';
import { auth, db } from '@/lib/firebase';
import PATH from '@/routes/path';
import { INVITE_STATUS } from '@/shared/constants/chat';
import { useTranslation } from '@/config/i18n';
import { getSystemMessage } from '@/lib/utils';
import DialogCustom from '@/components/common/ui/dialog';

type Props = {
  selectedConversation?: ConversationType | null;
  users: UserType[];
  openPopup: boolean;
  setOpenPopup: (val: boolean) => void;
};

export const ConfirmJoinGroup: React.FC<Props> = ({
  selectedConversation,
  users,
  openPopup,
  setOpenPopup,
}) => {
  const currentUser = auth.currentUser;
  const navigate = useNavigate();
  const [t] = useTranslation('');
  const conversationLink = `${PATH.chat}/${PATH.chat_conversation}`;
  const getUserInfo = (id: number) => users?.find((item) => item.id === id);
  const filteredUser = useMemo(() => {
    return getUserInfo(Number(currentUser?.uid));
  }, [users, currentUser]);

  const partnerName = useMemo(() => {
    return getUserInfo(Number(selectedConversation?.admin_id));
  }, [users, selectedConversation]);

  // Both names below are read out of `users`, the async `chat-participants` list. Acting before
  // it arrives does real damage: the invitation sentence renders as a decapitated
  // 「があなたを…グループに招待しました。」, and worse, accepting writes the join system message with
  // both names interpolated as empty strings - 「がをグループに追加しました。」 - straight into
  // Firestore, where it stays forever for every member to read (reproduced live 2026-08-26).
  // So the body waits behind a skeleton until the two names it actually needs are resolved.
  const isNamesReady = Boolean(partnerName && filteredUser);

  // ...but never indefinitely. If those records can genuinely not be resolved (the request
  // failed, the account was deleted), a permanent skeleton would leave this member unable to
  // either join OR decline - trading a cosmetic bug for a dead end. After the grace period the
  // buttons come back and the worst case is the old blank-name behaviour.
  const [waitedForNames, setWaitedForNames] = useState(false);
  useEffect(() => {
    if (!openPopup || isNamesReady) return;
    const timer = setTimeout(() => setWaitedForNames(true), 15_000);
    return () => clearTimeout(timer);
  }, [openPopup, isNamesReady]);

  const showSkeleton = !isNamesReady && !waitedForNames;

  const sendMessage = useCallback(
    async (content: MessageType) => {
      if (!selectedConversation?.id || !currentUser) return;

      const messagesRef = collection(
        db,
        'conversations',
        selectedConversation.id,
        'messages',
      );

      const response = doc(messagesRef);
      await setDoc(response, { id: response.id, ...content });
    },
    [selectedConversation?.id, currentUser],
  );

  const conversationDocRef = doc(
    db,
    'conversations',
    String(selectedConversation?.id),
  );

  const handleOnAccept = () => {
    if (!selectedConversation?.id || !currentUser) return;

    try {
      updateDoc(conversationDocRef, {
        [`invitation_statuses.${currentUser.uid}.status`]: INVITE_STATUS.ACCEPT,
      });
      const systemMessage: MessageType = getSystemMessage(
        Number(currentUser.uid),
        `${t('chat.add_to_group', {
          owner_name: partnerName?.full_name_kanji,
          member_name: filteredUser?.full_name_kanji,
        })}`,
      );
      sendMessage(systemMessage);
    } catch (error) {
      console.error('Error updating invite_statuses:', error);
    }
  };

  const handleOnDecline = useCallback(() => {
    if (!selectedConversation?.id || !currentUser) return;

    try {
      const systemMessage: MessageType = getSystemMessage(
        Number(currentUser.uid),
        `${filteredUser?.full_name_kanji}${t('chat.declined_to_group')}`,
      );
      sendMessage(systemMessage);
      updateDoc(conversationDocRef, {
        [`invitation_statuses.${currentUser.uid}`]: deleteField(),
        [`read_statuses.${currentUser.uid}`]: deleteField(),
        member_ids: arrayRemove(Number(currentUser.uid)),
      });
      navigate(conversationLink);
    } catch (error) {
      console.error('Error updating invite_statuses:', error);
    }
  }, [selectedConversation?.id, currentUser]);

  const onHidePopup = () => {
    setOpenPopup(false);
    navigate(conversationLink);
  };

  return (
    <DialogCustom
      header={t('chat.confirm_join_group_title')}
      visible={openPopup}
      onHide={onHidePopup}
      className="min-w-[396px] max-w-[396px]"
      contentClassName="border-b"
      headerClassName="border-b"
      footer={
        <div className="field">
          <div className="flex justify-end">
            <ButtonCustom
              className="mr-3 w-full rounded-2xl"
              type="button"
              severity="secondary"
              onClick={handleOnDecline}
              disabled={showSkeleton}
            >
              {t('chat.reject')}
            </ButtonCustom>
            <ButtonCustom
              type="button"
              className="w-full rounded-2xl"
              onClick={handleOnAccept}
              disabled={showSkeleton}
            >
              {t('chat.accept')}
            </ButtonCustom>
          </div>
        </div>
      }
    >
      <div className="flex justify-center items-center flex-col mt-5">
        {showSkeleton ? (
          <div className="w-full animate-pulse" data-testid="join-group-skeleton">
            <div className="h-4 w-full rounded bg-gray-300" />
            <div className="mt-2 h-4 w-3/4 rounded bg-gray-300" />
          </div>
        ) : (
          <p className="text-gray-700 text-base">
            {`${t('chat.confirm_join_group_msg', { user_name: partnerName?.full_name_kanji, group_name: selectedConversation?.name })}`}
          </p>
        )}
      </div>
    </DialogCustom>
  );
};
